import { useEffect, useRef } from "react";
import { CandlestickSeries, LineSeries, createChart, createSeriesMarkers } from "lightweight-charts";
import { palette } from "./pageStyles";

const LINE_COLORS = ["#a6c8ff", "#a497e6", "#6fbfb3", "#c98aa6", "#5fe0a8", "#9cb874", "#ff7d8c", "#eceef1"];

/** Decimals to show for a price level (EURUSD 5, USDJPY 3, gold 2 ...), judged from the last close. */
function pricePrecision(candles) {
  const last = candles && candles.length ? Math.abs(candles[candles.length - 1].close) : 1;
  if (last >= 1000) return 2;
  if (last >= 100) return 3;
  if (last >= 10) return 4;
  return 5;
}

export function seriesColor(series, index) {
  return series.color || LINE_COLORS[index % LINE_COLORS.length];
}

/** Candles plus whatever the Python code draws with plot(...) and signals(...).
 *  output = {series: [{name, pane: "price" | "oscillator", color, data: [{time, value}]}], markers: [{time, side, text}]} */
export default function PythonChart({ candles, output, height = 380 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const markersRef = useRef(null);
  const extraSeriesRef = useRef([]);

  useEffect(() => {
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { color: "#050608" }, textColor: "#9097a3", attributionLogo: true },
      grid: { vertLines: { color: "rgba(26, 37, 56,0.8)" }, horzLines: { color: "rgba(26, 37, 56,0.8)" } },
      rightPriceScale: { borderColor: palette.border },
      timeScale: { borderColor: palette.border, timeVisible: true, secondsVisible: false },
      crosshair: { mode: 0 },
    });
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#5fe0a8",
      downColor: "#ff7d8c",
      wickUpColor: "#5fe0a8",
      wickDownColor: "#ff7d8c",
      borderVisible: false,
    });
    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    markersRef.current = createSeriesMarkers(candleSeries, []);
    return () => {
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      markersRef.current = null;
      extraSeriesRef.current = [];
    };
  }, []);

  // candles
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    const rows = [];
    let lastTime = -1;
    [...(candles || [])]
      .sort((a, b) => a.time - b.time)
      .forEach((c) => {
        if (c.time === lastTime) return;
        lastTime = c.time;
        rows.push({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close });
      });
    const precision = pricePrecision(candles);
    series.applyOptions({ priceFormat: { type: "price", precision, minMove: Math.pow(10, -precision) } });
    series.setData(rows);
    chartRef.current?.timeScale().fitContent();
  }, [candles]);

  // lines + arrows from the Python code
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    extraSeriesRef.current.forEach((s) => {
      try {
        chart.removeSeries(s);
      } catch {
        // already gone
      }
    });
    extraSeriesRef.current = [];
    markersRef.current?.setMarkers([]);
    if (!output) return;

    (output.series || []).forEach((item, index) => {
      const onOscillator = item.pane === "oscillator";
      const line = chart.addSeries(
        LineSeries,
        {
          color: seriesColor(item, index),
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
          title: onOscillator ? item.name : "",
          crosshairMarkerVisible: false,
          ...(onOscillator
            ? {}
            : { priceFormat: { type: "price", precision: pricePrecision(candles), minMove: Math.pow(10, -pricePrecision(candles)) } }),
        },
        onOscillator ? 1 : 0
      );
      const seen = new Set();
      const data = (item.data || [])
        .filter((p) => p && Number.isFinite(p.value) && !seen.has(p.time) && seen.add(p.time))
        .sort((a, b) => a.time - b.time);
      line.setData(data);
      extraSeriesRef.current.push(line);
    });

    try {
      const panes = chart.panes();
      if (panes.length > 1 && panes[1].setStretchFactor) panes[1].setStretchFactor(0.45);
    } catch {
      // older chart versions: default pane sizes are fine
    }

    const markers = [...(output.markers || [])]
      .filter((m) => Number.isFinite(m.time))
      .sort((a, b) => a.time - b.time)
      .map((m) =>
        m.side === "sell"
          ? { time: m.time, position: "aboveBar", color: "#ff7d8c", shape: "arrowDown", text: m.text || "" }
          : { time: m.time, position: "belowBar", color: "#5fe0a8", shape: "arrowUp", text: m.text || "" }
      );
    markersRef.current?.setMarkers(markers);
  }, [output, candles]);

  return (
    <div style={{ position: "relative", border: `1px solid ${palette.border}`, borderRadius: "8px", overflow: "hidden" }}>
      <div ref={containerRef} style={{ height: typeof height === "number" ? `${height}px` : height, width: "100%" }} />
      {(!candles || candles.length === 0) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            color: palette.mutedText,
            fontSize: "0.85rem",
            pointerEvents: "none",
          }}
        >
          Press "Load candles" to see the chart
        </div>
      )}
    </div>
  );
}
